import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Bot, RefreshCw, Zap } from "lucide-react";

export const BotStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/admin/bot/status");
      setStatus(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Bot status check failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const activate = async () => {
    setBusy(true);
    try {
      await api.post("/admin/bot/activate");
      toast.success("Bot activated — webhook now points to this site");
      await check();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Activation failed");
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="card-void p-6 col-span-2 md:col-span-4" data-testid="bot-status-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Bot className="text-orange-400 mb-2" size={18} aria-hidden="true" />
            <div className="text-slate-200 text-sm">{error ? `⚠️ ${error}` : "Checking Wingman bot…"}</div>
          </div>
          {error && (
            <button
              onClick={check}
              disabled={busy}
              data-testid="bot-status-retry"
              className="px-4 py-2 rounded-full text-sm font-semibold text-slate-200 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} aria-hidden="true" /> Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!status.configured) {
    return (
      <div className="card-void p-6 col-span-2 md:col-span-4" data-testid="bot-status-card">
        <Bot className="text-orange-400 mb-3" size={18} aria-hidden="true" />
        <div className="text-slate-200 text-sm">Telegram bot is not configured in this environment.</div>
      </div>
    );
  }

  const live = status.live_here;
  return (
    <div className="card-void p-6 col-span-2 md:col-span-4" data-testid="bot-status-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="text-orange-400" size={18} aria-hidden="true" />
            <span className="text-[11px] uppercase tracking-widest text-slate-200 font-semibold">Wingman Bot</span>
            <span
              data-testid="bot-status-badge"
              className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${live ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}
            >
              {live ? "● LIVE on this site" : "● NOT connected to this site"}
            </span>
          </div>
          <div className="text-sm text-slate-200 truncate">
            @{status.bot_username} · webhook: <span className={live ? "text-emerald-300" : "text-red-300"}>{status.webhook_url || "not set"}</span>
          </div>
          {status.last_error && (
            <div className="text-xs text-red-300/80 mt-1 truncate" data-testid="bot-last-error">Last error: {status.last_error}</div>
          )}
          {status.pending_updates > 0 && (
            <div className="text-xs text-slate-300 mt-1">{status.pending_updates} update(s) waiting</div>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={check}
            disabled={busy}
            data-testid="bot-status-refresh"
            className="px-4 py-2 rounded-full text-sm font-semibold text-slate-200 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} aria-hidden="true" /> Check
          </button>
          {!live && !status.is_dev_env && (
            <button
              onClick={activate}
              disabled={busy}
              data-testid="bot-activate-button"
              className="px-4 py-2 rounded-full text-sm font-bold bg-orange-500 text-white hover:bg-orange-400 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <Zap size={14} aria-hidden="true" /> Activate bot here
            </button>
          )}
          {!live && status.is_dev_env && (
            <span className="text-xs text-slate-300 self-center max-w-[220px]">Dev environment — activate from the live site's admin panel</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default BotStatusCard;
